import ApiMApi from '@/api/apim';
import LiveApi from '@/api/live';
import StaticFileApi from '@/api/staticfile';
import StbbsApi from '@/api/stbbs';
import MyApi from '@/api/my';
import {getOneMinuteTimeStamp, getFiveSecondTimeStamp} from '@/utils/main';

/**
 * API뒤에 timestamp 를 붙여서 호출하는 API들이 있는데, 해당 API 들은 .js 로 내려주는 정적인 응답값이 대부분인데
 * 해당 API 는 cdn 을 통해 캐싱된 데이터를 내려주기 때문에 캐시 방지를 위해 각각의 주기가 정해져있다.
 * */

const MyPlusApi = {
  /**
   * 메인 섹션에서 필터링할 스트리머 리스트
   * 슴긴 스트리머, 추천받지 않기, 블랙한 스트리머, 정지 스트리머
   * */
  async getBlockStreamerAllList() {
    try {
      const response = await LiveApi.get(
        '/api/myplus/personalBlockController.php',
      );
      return response;
    } catch (error) {
      throw error;
    }
  },

  /**
   * 인기 모드 섹션별 방송 리스트 가져오기(성인방송까지)
   *
   */
  async getPopularListWithAdult() {
    const isMain = process.env.BUILD_ENV === 'main';

    try {
      const response = await StaticFileApi.get(
        !isMain
          ? `/pc/ko_KR/test_main_popular_with_adult_list.js?${getFiveSecondTimeStamp()}`
          : `/pc/ko_KR/main_popular_with_adult_list.js?${getFiveSecondTimeStamp()}`,
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * 어드민 인기 모드 세팅 리스트 조회
   *
   * */
  async getAdminPopularSettingList() {
    const isMain = process.env.BUILD_ENV === 'main';
    try {
      const response = await StaticFileApi.get(
        !isMain
          ? `/main/test_pc_popular_setting.js?${getOneMinuteTimeStamp()}`
          : `/main/pc_popular_setting.js?${getOneMinuteTimeStamp()}`,
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * 어드민 선호 모드 세팅 리스트 조회
   *
   *
   */
  async getAdminPreferSettingList() {
    const isMain = process.env.BUILD_ENV === 'main';
    try {
      const response = await StaticFileApi.get(
        !isMain
          ? `/main/test_pc_prefer_setting.js?${getOneMinuteTimeStamp()}`
          : `/main/pc_prefer_setting.js?${getOneMinuteTimeStamp()}`,
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * 선호 방송 리스트 가져오기
   * @param {String} szRelationType - 선호타입 ABCD 이런식으로 api 에서 내려줬었던 값
   * @returns {Promise<Object>}
   */
  async getPreferLiveVodList(queryParams) {
    try {
      const response = await LiveApi.get(
        '/api/myplus/preferbjLiveVodController.php',
        queryParams,
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * 정적 핫이슈,배너정보 등 가져오기
   * @returns {Promise<Object>}
   */
  async getStaticBroadListMainInfo() {
    const isMain = process.env.BUILD_ENV === 'main';

    try {
      //원래는 StaticFileApi
      const response = await StaticFileApi.get(
        !isMain
          ? `/main/test_static_list.js?${getOneMinuteTimeStamp()}`
          : `/main/static_list.js?${getOneMinuteTimeStamp()}`,
      );
      return response;
    } catch (error) {
      throw error;
    }
  },

  /**
   * 선호 카테고리 리스트 가져오는 api
   * @param {String} type - prefer/popular
   * @returns {Promise<Object>}
   */
  async getPreferCategoryList(param) {
    try {
      const response = await LiveApi.get(
        '/api/myplus/preferCategoryPostController.php',
        param,
      );
      return response;
    } catch (error) {
      throw error;
    }
  },

  /**
   * 선호 캐치 리스트 가져오는 api
   * @returns {Promise<Object>}
   * */
  async getPreferCatchList() {
    try {
      const response = await LiveApi.get(
        '/api/myplus/myplusCatchController.php',
      );
      return response;
    } catch (error) {
      throw error;
    }
  },

  /**
   * 선호 캐치스토리 리스트 가져오는 api
   * @returns {Promise<Object>}
   * */
  async getPreferCatchStoryList() {
    try {
      const response = await LiveApi.get(
        '/api/myplus/myplusCatchStoryController.php',
      );
      return response;
    } catch (error) {
      throw error;
    }
  },

  /**
   * 배너 알람 등록 api
   * @param {Number} alarm_idx - 배너 인덱스값
   * @param {string} platform - PC
   * @returns {Promise<Object>}
   * */
  async setTopBannerAlarm(params) {
    try {
      const response = await ApiMApi.post('/push/alarm/a/register', params);
      return response;
    } catch (error) {
      throw error;
    }
  },

  /**
   * 배너 알람 등록 여부 조회 api
   * @param {Number} alarm_idx - 배너 인덱스값
   * @returns {Promise<Object>}
   * */
  async setCheckTopBannerAlarm(params) {
    try {
      const response = await ApiMApi.post('/push/alarm/a/status', params);
      return response;
    } catch (error) {
      throw error;
    }
  },

  /**
   * 배너 알람 등록 여부 조회 api
   * @param {Number} alarm_idx - 배너 인덱스값
   * @returns {Promise<Object>}
   * */
  async setDeleteTopBannerAlarm(params) {
    try {
      const response = await ApiMApi.post('/push/alarm/a/unregister', params);
      return response;
    } catch (error) {
      throw error;
    }
  },

  /**
   * 영상 스트리밍 리스트 가져오는 api
   * @returns {Promise<Object>}
   * */
  async getMainTopLiveList() {
    try {
      const response = await LiveApi.get('/api/get_main_top_live_list.php');
      return response;
    } catch (error) {
      throw error;
    }
  },

  /**
   * 나중에 보기 추가하기
   * @param {String} type - LIVE/VOD
   * @param {Number} broadNo - 방송번호/타이틀번호
   * @returns {Promise<Object>}
   */
  async setLaterView(type, broadNo) {
    try {
      const response = await MyApi.post(
        `/api/history/later/${type}/${broadNo}`,
      );
      return response;
    } catch (error) {
      throw error;
    }
  },

  /**
   * 추천받지 않기 추가
   * @param {String} bj_id - 스트리머 아이디
   * @param {String} bj_nick - 스트리머 닉네임
   * @param {String} type- LIVE/VOD/CATCH
   * @param {String} list_data_type - ex) pbj_live_score
   * @param {Number} broad_no - 방송번호
   * @param {Number} title_no - 영상번호
   * @param {String} os - PC
   * @param {String} namespace - ex) prflvod
   * @param {String} key - 캐시방지
   * @returns {Promise<Object>}
   */
  async setNoRecommendStreamer(params) {
    try {
      const response = await ApiMApi.post('/broad/a/hidebj', params);
      return response;
    } catch (error) {
      throw error;
    }
  },

  /**
   * 추천받지 않기 취소
   * @param {String} bj_id - 스트리머 아이디
   * @param {String} type- LIVE/VOD/CATCH
   * @param {String} list_data_type - ex) pbj_live_score
   * @param {Number} broad_no - 방송번호
   * @param {Number} title_no - 영상번호
   * @param {Number} score - 점수
   * @returns {Promise<Object>}
   */
  async setNoRecommendUndoStreamer(params) {
    try {
      const response = await ApiMApi.post('/broad/a/hidebjdel', params);
      return response;
    } catch (error) {
      throw error;
    }
  },

  /**
   * 스트리머 숨기기 추가
   * @param {String} bj_id - 스트리머 아이디
   * @param {String} bj_nick - 스트리머 닉네임
   * @param {String} type- LIVE/VOD/CATCH
   * @param {String} list_data_type - ex) pbj_live_score
   * @param {String} location - ex) myplus , all , bora, game, sports?
   * @param {String} broad_no - 방송번호
   * @param {String} title_no - 영상번호
   * @param {String} story_idx - 스토리번호
   * @param {String} os - PC
   * @param {String} namespace - ex) prflvod
   * @param {String} key - 캐시방지
   * @returns {Promise<Object>}
   */
  async setHiddenStreamer(params) {
    try {
      const response = await ApiMApi.post('/broad/hiddenbj/a/add', params);
      return response;
    } catch (error) {
      throw error;
    }
  },

  /**
   * 스트리머 숨기기 취소
   * @param {String} bj_id - 스트리머 아이디
   * @param {String} type- LIVE/VOD/CATCH
   * @param {String} list_data_type - ex) pbj_live_score
   * @param {String} location - ex) myplus , all , bora, game, sports?
   * @param {String} broad_no - 방송번호
   * @param {String} title_no - 영상번호
   * @param {String} story_idx - 스토리번호
   * @returns {Promise<Object>}
   */
  async setHiddenUndoStreamer(params) {
    try {
      const response = await ApiMApi.post('/broad/hiddenbj/a/remove', params);
      return response;
    } catch (error) {
      throw error;
    }
  },

  /**
   * 재생목록에 담기
   * @param {String} szWork - get_making_playlist
   * @param {String} type- all
   * @param {String} sorttype - ex) recent_date
   * @param {Number} title_no - 영상 번호
   * @param {Number} nPage - 페이지번호
   * @param {Number} nLimit - 노출갯수
   * @returns {Promise<Object>}
   */
  async getPlayList(params) {
    try {
      const response = await StbbsApi.post('/vodplaylist/api/api.php', params);
      return response;
    } catch (error) {
      throw error;
    }
  },

  /**
   * 재생목록 만들기
   * @param {String} szWork - ins_playlist_list
   * @param {String} display- 공개/비공개 여부
   * @param {String} title - 재생목록 이름
   * @param {String} share_yn - 공유 여부
   * @returns {Promise<Object>}
   */
  async setCreatePlayList(params) {
    try {
      const response = await StbbsApi.post('/vodplaylist/api/api.php', params);
      return response;
    } catch (error) {
      throw error;
    }
  },

  /**
   * 재생목록 추가
   * @param {String} szWork - ins_playlist_info
   * @param {Number[]} nPlaylistIdx[]- 재생목록 인덱스
   * @param {String} title_no - 재생목록 이름
   * @returns {Promise<Object>}
   */
  async setAddPlayList(params) {
    try {
      const response = await StbbsApi.post('/vodplaylist/api/api.php', params);
      return response;
    } catch (error) {
      throw error;
    }
  },
  /**
   * 재생목록 퍼가기
   * @param {number} playlistIdx - 재생목록 번호
   * @returns {Promise<Object>}
   */
  async setSharedPlaylist(playlistIdx) {
    if (!playlistIdx) {
      return;
    }
    try {
      const data = await StbbsApi.post('/vodplaylist/api/api.php', {
        szWork: 'share_playlist_bj_list',
        nPlaylistIdx: playlistIdx,
      });
      return data;
    } catch (e) {
      console.error(e, 'share_playlist error');
    }
  },

  /**
   * 인기 캐치 리스트
   * @returns {Promise<Object>}
   */
  async getPopularCatchList() {
    try {
      const response = await LiveApi.get(
        '/api/myplus/popularCatchController.php',
      );
      return response;
    } catch (error) {
      throw error;
    }
  },
};
export default MyPlusApi;
